const EARTH_RADIUS_MILES = 3958.8

const toRadians = (deg) => (deg * Math.PI) / 180

export function haversineDistance(lat1, lng1, lat2, lng2) {
  const dLat = toRadians(lat2 - lat1)
  const dLng = toRadians(lng2 - lng1)
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLng / 2) ** 2
  return EARTH_RADIUS_MILES * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

// Anchor the first arrival to startMin by subtracting the start→first-item
// travel from every arrival and from totalMinutes. Used whenever the user
// picked a specific start time (rather than "now"), so the time they picked
// is honored as the moment they reach the first attraction.
export function stripVirtualStartTravel(result, startMin) {
  if (!result || result.arrivals.length === 0) return result
  const offset = result.arrivals[0] - startMin
  if (offset === 0) return result
  const dists = result.legDistances || []
  return {
    ...result,
    arrivals: result.arrivals.map((a) => a - offset),
    totalMinutes: result.totalMinutes - offset,
    totalDistanceMiles: Math.max(0, (result.totalDistanceMiles || 0) - (dists[0] || 0)),
    legDistances: dists.map((d, i) => (i === 0 ? 0 : d)),
  }
}

export function formatMinutesToTime(min) {
  const d = new Date(2000, 0, 1, Math.floor(min / 60) % 24, Math.floor(min % 60))
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
}

function formatMiles(d, showZero = false) {
  return d > 0 || (showZero && d >= 0) ? ` · ${d.toFixed(2)} mi` : ""
}

function formatEtaText(arrivalMin, distanceMi, position) {
  const prefix = position ? `${position} · ` : ""
  return prefix + formatMinutesToTime(arrivalMin) + formatMiles(distanceMi, position === 1)
}

export function parseOptimizeResult(data) {
  return {
    order: data.order,
    arrivals: data.arrivals,
    totalMinutes: data.total_minutes,
    pastCloseCount: data.past_close_count || 0,
    totalDistanceMiles: data.total_distance_miles || 0,
    legDistances: data.leg_distances || [],
    effectiveTimes: data.effective_times || [],
    startMin: data.start_min ?? null,
  }
}

export function insertLegEta(card, arrivalMin, position, distanceMi) {
  const eta = document.createElement("div")
  eta.className = "rodg-eta"
  eta.textContent = formatEtaText(arrivalMin, distanceMi || 0, position)
  card.appendChild(eta)
}

export function formatDuration(min) {
  const h = Math.floor(min / 60)
  const m = Math.round(min % 60)
  if (h > 0 && m > 0) return `~${h}h ${m}m`
  if (h > 0) return `~${h}h`
  return `~${m}m`
}

// "YYYY-MM-DDTHH:MM" in local time, the value format of datetime-local inputs.
function formatDatetimeLocal(d) {
  const pad = (n) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function formatRouteBanner(label, duration, startMin, timezoneAbbr, optimizedFor, totalDistanceMiles) {
  const head = label + formatDuration(duration) + formatMiles(totalDistanceMiles)
  if (!optimizedFor && startMin == null) return head
  const line = [
    `${formatMinutesToTime(startMin)} – ${formatMinutesToTime(startMin + duration)}`,
    timezoneAbbr,
    optimizedFor && `· ${optimizedFor}`,
  ]
    .filter(Boolean)
    .join(" ")
  return `${head}<br>${line}`
}

export function formatOptimizedFor(dateStr) {
  const d = new Date(dateStr)
  if (isNaN(d)) return null
  return d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric", year: "numeric" })
}

export function dateStringDaysFromToday(days) {
  const d = new Date()
  d.setDate(d.getDate() + days)
  return formatDatetimeLocal(d).split("T")[0]
}

export function todayDateString() {
  return dateStringDaysFromToday(0)
}

// How far ahead a visit may be planned. Deliberately past every park's published
// schedule horizon, so the bound the user meets is "that park hasn't announced
// hours yet" rather than an arbitrary cap of ours. Independent of the crowd
// calendar's forecast window, which is far shorter and gates a different thing.
const MAX_VISIT_DAYS = 365

// min/max on a date input only mark a value invalid — typed dates and dates
// restored from storage still arrive unchecked — so this is what actually holds
// the range. Both bounds move with the calendar day, hence recomputed per call.
function clampVisitDate(value) {
  const today = todayDateString()
  if (value < today) return today

  const max = dateStringDaysFromToday(MAX_VISIT_DAYS)
  return value > max ? max : value
}

export function initTimeDateInputs({
  timeEl,
  dateEl,
  hiddenEl,
  nowCheckbox,
  storageTimeKey,
  storageNowKey,
  propagateNowOff = false,
}) {
  function currentDate() {
    return dateEl ? dateEl.value : todayDateString()
  }

  function sync() {
    hiddenEl.value = `${currentDate()}T${timeEl.value}`
  }

  function setToNow() {
    const [date, time] = formatDatetimeLocal(new Date()).split("T")
    timeEl.value = time
    if (dateEl) dateEl.value = date
    sync()
  }

  function applyNowState() {
    if (nowCheckbox.checked) setToNow()
  }

  if (dateEl) {
    dateEl.min = todayDateString()
    dateEl.max = dateStringDaysFromToday(MAX_VISIT_DAYS)
  }

  const saved = localStorage.getItem(storageTimeKey)
  if (saved && saved.includes("T")) {
    const [date, time] = saved.split("T")
    timeEl.value = time
    if (dateEl) dateEl.value = date
    // Clamps the restored date, syncs the hidden field, and rewrites storage.
    // No event, so the "user touched it, drop Now" branch stays untaken.
    onTimeChange()
  } else {
    setToNow()
  }
  nowCheckbox.checked = localStorage.getItem(storageNowKey) !== "false"
  applyNowState()

  function onTimeChange(event) {
    if (dateEl && dateEl.value) dateEl.value = clampVisitDate(dateEl.value)
    sync()
    localStorage.setItem(storageTimeKey, hiddenEl.value)
    if (event?.isTrusted && nowCheckbox.checked) {
      nowCheckbox.checked = false
      if (propagateNowOff) {
        nowCheckbox.dispatchEvent(new Event("change"))
      } else {
        localStorage.setItem(storageNowKey, "false")
      }
    }
  }
  timeEl.addEventListener("change", onTimeChange)
  if (dateEl) dateEl.addEventListener("change", onTimeChange)

  nowCheckbox.addEventListener("change", function () {
    localStorage.setItem(storageNowKey, nowCheckbox.checked ? "true" : "false")
    applyNowState()
  })

  return { setToNow }
}
